import type { EventTypes } from '@racemap/sdk/schema/events';
import { NULL_TAG } from '@racemap/utilities/consts/events';
import { useMemo } from 'react';
import { ALL_TAG } from 'src/lib/player/constants';
import type { RenderInfoBuffer } from 'src/lib/player/RenderInfoManager';
import type { EventState, PoiType, StarterType, TrackType } from 'src/lib/player/store';
import { type SidebarItem, SidebarItemType, SidebarSection, type StarterSorting } from './types';
import { createSearchMatcher, deduplicateStarters, sortStarters } from './utils';

type UseSidebarItemsArgs = {
  searchText: string;
  openSections: Array<SidebarSection>;
  openStarterGroups: Array<string>;
  isFilterOpen: boolean;
  isSortOpen: boolean;
  starterSorting: StarterSorting | null;
  renderInfoBuffer: RenderInfoBuffer;
  showEventSelector: boolean;
  eventType: EventTypes;
  allEvents: Array<EventState>;
  allCurrentStarters: Array<StarterType>;
  allCurrentTracks: Array<TrackType>;
  allCurrentPois: Array<PoiType>;
  currentSelectedIds: Array<string>;
  availableTags: Map<string, Array<[string, number]>>;
  availableStarterGroups: Map<string, Array<[string, number]>>;
  filteredStarters: Array<StarterType>;
  deduplicatedStarters: Array<StarterType>;
  showFavoriteSection: boolean;
};

export function useSidebarItems({
  searchText,
  openSections,
  openStarterGroups,
  isFilterOpen,
  isSortOpen,
  starterSorting,
  renderInfoBuffer,
  showEventSelector,
  eventType,
  allEvents,
  allCurrentStarters,
  allCurrentTracks,
  allCurrentPois,
  currentSelectedIds,
  availableTags,
  availableStarterGroups,
  filteredStarters,
  deduplicatedStarters,
  showFavoriteSection,
}: UseSidebarItemsArgs): Array<SidebarItem> {
  return useMemo(() => {
    if (searchText === '') {
      const items: Array<SidebarItem> = [];
      if (showFavoriteSection) {
        items.push({
          type: SidebarItemType.HEADER,
          label: SidebarSection.FAVORITE,
        });
      }
      if (openSections.includes(SidebarSection.FAVORITE)) {
        for (const track of allCurrentTracks.filter((poi) => currentSelectedIds.includes(poi.id))) {
          items.push({ type: SidebarItemType.TRACK, track, isFavorite: true });
        }
        for (const poi of allCurrentPois.filter((poi) => currentSelectedIds.includes(poi.id))) {
          items.push({ type: SidebarItemType.POI, poi, isFavorite: true });
        }
        for (const starter of deduplicateStarters(
          allCurrentStarters.filter((starter) => currentSelectedIds.includes(starter.id)),
        )) {
          items.push({ type: SidebarItemType.STARTER, starter, isFavorite: true });
        }
      }
      if (showEventSelector) {
        items.push({ type: SidebarItemType.HEADER, label: SidebarSection.EVENTS, eventType });
        if (openSections.includes(SidebarSection.EVENTS)) {
          for (const event of allEvents) {
            items.push({ type: SidebarItemType.EVENT, event });
          }
        }
      }
      if (allCurrentTracks.length > 0) {
        items.push({ type: SidebarItemType.HEADER, label: SidebarSection.TRACK });
        if (openSections.includes(SidebarSection.TRACK)) {
          for (const track of allCurrentTracks) {
            items.push({ type: SidebarItemType.TRACK, track });
          }
        }
      }
      if (allCurrentPois.length > 0) {
        items.push({ type: SidebarItemType.HEADER, label: SidebarSection.POI });
        if (openSections.includes(SidebarSection.POI)) {
          for (const poi of allCurrentPois) {
            items.push({ type: SidebarItemType.POI, poi });
          }
        }
      }
      if (allCurrentStarters.length > 0) {
        items.push({ type: SidebarItemType.HEADER, label: SidebarSection.STARTERS });
        if (openSections.includes(SidebarSection.STARTERS)) {
          if (isFilterOpen && availableTags.size > 0) {
            items.push({ type: SidebarItemType.STARTER_FILTER, itemCount: availableTags.size });
          }
          if (isSortOpen) {
            items.push({ type: SidebarItemType.STARTER_SORT });
          }
          if (availableStarterGroups.size > 0) {
            for (const [tagKey, tagValues] of availableStarterGroups) {
              for (const [tagValue] of tagValues) {
                items.push({ type: SidebarItemType.STARTER_GROUP, tagKey, tagValue });
                if (openStarterGroups.includes(`${tagKey}-${tagValue}`)) {
                  const startersInGroup = sortStarters(
                    deduplicatedStarters.filter(
                      (starter) =>
                        starter.tags.get(tagKey) === tagValue ||
                        (tagValue === NULL_TAG && !starter.tags.has(tagKey)),
                    ),
                    starterSorting,
                    renderInfoBuffer,
                  );
                  for (const starter of startersInGroup) {
                    items.push({
                      type: SidebarItemType.STARTER,
                      starter,
                      group: `${tagKey}-${tagValue}`,
                    });
                  }
                }
              }
            }
            items.push({ type: SidebarItemType.STARTER_GROUP, tagKey: ALL_TAG, tagValue: '' });
            if (openStarterGroups.includes(`${ALL_TAG}-`)) {
              for (const starter of sortStarters(
                deduplicatedStarters,
                starterSorting,
                renderInfoBuffer,
              )) {
                items.push({
                  type: SidebarItemType.STARTER,
                  starter,
                  group: `${ALL_TAG}-`,
                });
              }
            }
          } else {
            for (const starter of sortStarters(
              deduplicatedStarters,
              starterSorting,
              renderInfoBuffer,
            )) {
              items.push({ type: SidebarItemType.STARTER, starter });
            }
          }
        }
      }
      return items;
    }

    const matchesSearch = createSearchMatcher(searchText);

    const searchFilteredStarters = filteredStarters.filter((starter) =>
      matchesSearch(starter.searchText),
    );
    const filteredTracks = allCurrentTracks.filter((track) => matchesSearch(track.name));
    const filteredPois = allCurrentPois.filter((poi) => matchesSearch(poi.name));

    const items: Array<SidebarItem> = [];

    if (filteredTracks.length > 0) {
      items.push({ type: SidebarItemType.HEADER, label: SidebarSection.TRACK });
    }
    for (const track of filteredTracks) {
      items.push({ type: SidebarItemType.TRACK, track });
    }

    if (filteredPois.length > 0) {
      items.push({ type: SidebarItemType.HEADER, label: SidebarSection.POI });
    }
    for (const poi of filteredPois) {
      items.push({ type: SidebarItemType.POI, poi });
    }

    if (searchFilteredStarters.length > 0) {
      items.push({ type: SidebarItemType.HEADER, label: SidebarSection.STARTERS });
    }
    if (isFilterOpen && availableTags.size > 0) {
      items.push({ type: SidebarItemType.STARTER_FILTER, itemCount: availableTags.size });
    }
    if (isSortOpen) {
      items.push({ type: SidebarItemType.STARTER_SORT });
    }
    if (openSections.includes(SidebarSection.STARTERS)) {
      for (const starter of deduplicateStarters(searchFilteredStarters)) {
        items.push({ type: SidebarItemType.STARTER, starter });
      }
    }

    return items;
  }, [
    searchText,
    openSections,
    openStarterGroups,
    isFilterOpen,
    isSortOpen,
    starterSorting,
    renderInfoBuffer,
    showEventSelector,
    eventType,
    allEvents,
    allCurrentStarters,
    allCurrentTracks,
    allCurrentPois,
    currentSelectedIds,
    availableTags,
    availableStarterGroups,
    filteredStarters,
    deduplicatedStarters,
  ]);
}
